import axios from 'axios';
import { SessionManager } from '../utils/SessionManager';
import { DEMO_MODE } from '../config';
import { demoAdapter } from '../demo/demoAdapter';
import { isInternalNetworkOnlyError } from '../utils/internalNetworkError';
import { UPLOAD } from '../config/constants';

// ① axiosインスタンスを作る
// timeout は30分にしている。
// このインスタンスはログイン・一覧・コメント等ほぼ全APIが通る共通インスタンスで、
// 分割アップロード（chunkUploadService.ts）は fetch/XMLHttpRequest で別タイムアウトを持ち、
// この axios を経由しない。ここを延ばしても20GB対応の効果は無く、他APIの失敗検出が遅れるだけ。
// maxContentLength/maxBodyLength はファイルサイズ上限の定数（Issue #411: 20GB対応）を参照する。
const axiosInstance = axios.create({
  baseURL: '/api', // 相対URLでnginxリバースプロキシ経由
  timeout: UPLOAD.VIDEO_UPLOAD_TIMEOUT,
  maxContentLength: UPLOAD.MAX_FILE_SIZE_BYTES,
  maxBodyLength: UPLOAD.MAX_FILE_SIZE_BYTES,
});

// デモモード時はバックエンドへ送らず、フィクスチャで応答するアダプタに差し替える。
// 本番（DEMO_MODE=false）では既定アダプタのまま、挙動は一切変わらない。
if (DEMO_MODE) {
  axiosInstance.defaults.adapter = demoAdapter;
}

// ② リクエストインターセプター (トークン付与)
axiosInstance.interceptors.request.use(
  config => {
    const activeSession = SessionManager.getActiveSession();
    if (activeSession && activeSession.token) {
      config.headers.Authorization = `Bearer ${activeSession.token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// ③ レスポンスインターセプター (401/403 → unauthorized イベント)
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      const { status } = error.response;
      const url = error.config?.url || '';

      // X-Skip-Auth-Error ヘッダーが設定されている場合は認証エラー処理をスキップ
      const skipAuthError = error.config?.headers?.['X-Skip-Auth-Error'] === 'true';

      // パスワード変更エンドポイントの401/403はセッションクリアしない
      const isPasswordChangeEndpoint = url.includes('/auth/change-password');

      // 接続元ゲート（requireInternalNetwork）の 403 はセッションクリアしない。
      // これは「認証切れ」ではなく院外からの操作を拒否したポリシー応答であり、
      // セッションを破棄すると院外ユーザーが操作のたびにログアウトさせられる。
      // 例: 院外から処理中動画を削除 → DELETE /videos/upload/:sessionId が
      //     403 INTERNAL_NETWORK_ONLY → ここでログアウトしていた。
      //
      // TODO(403の扱い): utils/axiosInterceptor.js は #334 で「403 はセッション破棄しない」
      //   （認可エラーであって認証失効ではない）に倒した。本来こちらも揃えるべきだが、
      //   jwtAuthenticate は無効・期限切れトークンに 403 'Invalid token' を返す
      //   （backend/src/middlewares/jwtAuthenticate.ts）。axiosInstance はアプリの API 呼び出し
      //   ほぼ全てを通るため、ここで 403 を一律スキップするとトークン期限切れのユーザーが
      //   ログイン画面へ誘導されず、全リクエストが失敗し続ける状態で詰む。
      //   揃えるなら jwtAuthenticate の 403 → 401 とセットで行うこと（別PR）。
      if (
        (status === 401 || status === 403) &&
        !isPasswordChangeEndpoint &&
        !skipAuthError &&
        !isInternalNetworkOnlyError(error)
      ) {
        const activeSession = SessionManager.getActiveSession();
        if (activeSession) {
          SessionManager.removeSession(activeSession.userId);
        }
        window.dispatchEvent(new CustomEvent('unauthorized'));
      } else if (status === 403 && isInternalNetworkOnlyError(error)) {
        // 「本来ログアウトさせる応答を意図的に見逃した」分岐を無言にしない。
        // useOriginAccess と同じ方針（UI 挙動は変えないが痕跡は残す）。
        console.warn(
          '[api] 接続元ゲート(403 INTERNAL_NETWORK_ONLY)のためセッション破棄をスキップしました',
          { url, status }
        );
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
